<template>
  <div class="base-integration-missing">
    <img
      :src="backgroundImage"
      class="background-image"
      :class="emptyStateVariant"
      aria-hidden="true"
    />
    <div class="content">
      <p class="title">{{ title }}</p>
      <p class="description">{{ description }}</p>
      <template v-if="!synchronizing">
        <BaseButton
          kind="main"
          type="primary"
          :text="ctaText"
          @click="goToIntegration"
        >
          <template #icon>
            <GoogleIcon />
          </template>
        </BaseButton>
        <BaseButton
          kind="main"
          type="primary"
          :text="outlookCtaText"
          state="disabled"
        >
          <template #icon>
            <MicrosoftOutlookIcon />
          </template>
        </BaseButton>
        <BaseButton
          kind="main"
          type="primary"
          :text="appleCtaText"
          state="disabled"
        >
          <template #icon>
            <AppleCalendarIcon />
          </template>
        </BaseButton>
      </template>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType } from "vue";

import emptyStateCalendarDark from "@/assets/illus/empty-state-calendar--dark.png";
import emptyStateCalendarLight from "@/assets/illus/empty-state-calendar--light.png";
import emptyStateContactsDark from "@/assets/illus/empty-state-contacts--dark.png";
import emptyStateContactsLight from "@/assets/illus/empty-state-contacts--light.png";
import emptyStatePlannerDark from "@/assets/illus/empty-state-planner--dark.png";
import emptyStatePlannerLight from "@/assets/illus/empty-state-planner--light.png";
import BaseButton from "@/components/base/BaseButton.vue";
import AppleCalendarIcon from "@/components/icons/apps/AppleCalendarIcon.vue";
import GoogleIcon from "@/components/icons/apps/google/GoogleIcon.vue";
import MicrosoftOutlookIcon from "@/components/icons/apps/MicrosoftOutlookIcon.vue";
import * as inject from "@/components/inject";
import { RouteNames } from "@/routes/types";
import { navigateTo } from "@/utils/navigate";

export type IntegrationMissingFeature = "calendar" | "contacts";

const FEATURE_CONFIG: Record<
  IntegrationMissingFeature,
  {
    title: string;
    description: string;
    syncTitle: string;
    syncDescription: string;
    ctaText: string;
    outlookCtaText: string;
    appleCtaText: string;
    route: RouteNames;
  }
> = {
  calendar: {
    title: "Calendar not connected",
    description: "Connect your calendar to see and manage events.",
    syncTitle: "Calendar synchronizing",
    syncDescription: "Your calendar events are being synchronized…",
    ctaText: "Google Calendar",
    outlookCtaText: "Outlook Calendar",
    appleCtaText: "Apple Calendar",
    route: RouteNames.SettingsIntegrationsGoogleCalendar,
  },
  contacts: {
    title: "Contacts not connected",
    description: "Connect your contacts to sync and search your contacts.",
    syncTitle: "Contacts synchronizing",
    syncDescription: "Your contacts are being synchronized…",
    ctaText: "Google Contacts",
    outlookCtaText: "Outlook Contacts",
    appleCtaText: "Apple Contacts",
    route: RouteNames.SettingsIntegrationsGoogleContacts,
  },
};

export default defineComponent({
  name: "BaseIntegrationMissing",
  components: {
    AppleCalendarIcon,
    BaseButton,
    GoogleIcon,
    MicrosoftOutlookIcon,
  },
  props: {
    feature: {
      type: String as PropType<IntegrationMissingFeature>,
      required: true,
    },
    synchronizing: {
      type: Boolean,
      default: false,
    },
    /** When feature is "calendar", which empty state image set to use. Agenda uses "calendar", Planner/settings use "planner". */
    emptyStateVariant: {
      type: String as PropType<"calendar" | "planner">,
      default: "calendar",
    },
  },
  setup(props) {
    const profile = inject.profile();
    const router = inject.router();
    const theme = inject.theme();

    const config = computed(() => FEATURE_CONFIG[props.feature]);

    const backgroundImage = computed(() => {
      const isDark = theme.value === "dark";
      if (props.feature === "calendar") {
        if (props.emptyStateVariant === "calendar") {
          return isDark ? emptyStateCalendarDark : emptyStateCalendarLight;
        }
        return isDark ? emptyStatePlannerDark : emptyStatePlannerLight;
      }
      return isDark ? emptyStateContactsDark : emptyStateContactsLight;
    });

    const title = computed(() =>
      props.synchronizing ? config.value.syncTitle : config.value.title
    );
    const description = computed(() =>
      props.synchronizing
        ? config.value.syncDescription
        : config.value.description
    );
    const ctaText = computed(() => config.value.ctaText);
    const outlookCtaText = computed(() => config.value.outlookCtaText);
    const appleCtaText = computed(() => config.value.appleCtaText);

    const goToIntegration = () => {
      const orgId = profile.value?.user.personal.organizationId;
      if (!orgId) return;
      navigateTo(config.value.route, router, {
        params: { orgId },
      });
    };

    return {
      backgroundImage,
      title,
      description,
      ctaText,
      outlookCtaText,
      appleCtaText,
      goToIntegration,
    };
  },
});
</script>

<style scoped lang="scss">
.base-integration-missing {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  text-align: center;
  height: 100%;
  min-height: 200px;
  overflow: hidden;

  .background-image {
    position: absolute;
    width: 468px;
    height: 568px;
    inset-inline-start: 50%;
    top: 50%;
    display: block;
    box-sizing: border-box;
    max-width: none;
    max-height: none;
    transform: translate(-50%, -50%);
    pointer-events: none;
    user-select: none;
    opacity: 0.7;

    &.planner {
      width: 795px;
      height: 568px;
      top: 45%;
    }
  }

  .content {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    row-gap: 12px;
    max-width: 320px;
    backdrop-filter: blur(20px);
    padding: 20px;
    border-radius: 12px;
  }

  .title {
    margin: 0;
    @extend %font-h4;
    @include themed() {
      color: t($primary);
    }
  }

  .description {
    margin: 0;
    max-width: 230px;
    margin-bottom: 24px;
    @extend %font-body-regular;

    @include themed() {
      color: t($grey-60);
    }
  }
}
</style>
